/**
 * Koji API Client
 */

import { auth } from './firebase';
import { getIdToken } from 'firebase/auth';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://koji-api.idoumahart.workers.dev';

/* ─── Helpers ─── */

async function getAuthHeader(): Promise<Record<string, string>> {
  const currentUser = auth?.currentUser;
  if (!currentUser) return {};
  try {
    const token = await getIdToken(currentUser, false);
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string }> {
  try {
    const authHeaders = await getAuthHeader();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // CSRF protection
      ...authHeaders,
      ...((options.headers as Record<string, string>) || {}),
    };
    const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || `HTTP ${response.status}` };
    }

    return data;
  } catch (err) {
    return { success: false, error: (err as Error).message || 'Network error' };
  }
}

/* ─── Auth ─── */

export const api = {
  auth: {
    signup: (payload: { email: string; password: string; fullName?: string; plan?: string }) =>
      request<{ message: string; userId: string }>('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    login: (payload: { email: string; password: string }) =>
      request<{ accessToken: string; refreshToken: string; user: { id: string; email: string; plan: string } }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    refresh: (refreshToken: string) =>
      request<{ accessToken: string; refreshToken: string }>('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),

    me: () =>
      request<{ id: string; email: string; plan: string; email_verified: number }>('/api/auth/me', {
        method: 'GET',
      }),

    verifyEmail: (token: string) =>
      request<{ message: string }>(`/api/auth/verify-email?token=${token}`, {
        method: 'GET',
      }),

    forgotPassword: (payload: { email: string }) =>
      request<{ message: string }>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    resetPassword: (payload: { token: string; password: string }) =>
      request<{ message: string }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateProfile: (payload: { name?: string; company?: string; role?: string; bio?: string; timezone?: string }) =>
      request<{ updated: boolean }>('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),

    getProfile: () =>
      request<Record<string, string>>('/api/auth/profile', {
        method: 'GET',
      }),
  },

  /* ─── Experiments ─── */

  experiments: {
    list: (siteId?: string) =>
      request<any[]>('/api/experiments' + (siteId ? `?site_id=${siteId}` : ''), { method: 'GET' }),

    create: (payload: any) =>
      request<any>('/api/experiments', { method: 'POST', body: JSON.stringify(payload) }),

    get: (id: string) =>
      request<any>(`/api/experiments/${id}`, { method: 'GET' }),

    update: (id: string, payload: any) =>
      request<any>(`/api/experiments/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),

    delete: (id: string) =>
      request<any>(`/api/experiments/${id}`, { method: 'DELETE' }),
  },

  /* ─── Analytics ─── */

  analytics: {
    track: (payload: any) =>
      request<any>('/api/analytics/track', { method: 'POST', body: JSON.stringify(payload) }),

    overview: (siteId: string) =>
      request<any>(`/api/analytics/overview?site_id=${siteId}`, { method: 'GET' }),
  },

  /* ─── Teams ─── */

  teams: {
    list: () =>
      request<any[]>('/api/teams', { method: 'GET' }),

    create: (payload: { name: string }) =>
      request<any>('/api/teams', { method: 'POST', body: JSON.stringify(payload) }),

    getMembers: (id: string) =>
      request<any>(`/api/teams/${id}/members`, { method: 'GET' }),

    invite: (id: string, payload: { email: string; role?: string }) =>
      request<any>(`/api/teams/${id}/invite`, { method: 'POST', body: JSON.stringify(payload) }),

    getWhiteLabel: (id: string) =>
      request<any>(`/api/teams/${id}/white-label`, { method: 'GET' }),

    updateWhiteLabel: (id: string, payload: any) =>
      request<any>(`/api/teams/${id}/white-label`, { method: 'PUT', body: JSON.stringify(payload) }),
  },

  /* ─── Stripe ─── */

  stripe: {
    createCheckout: (payload: { priceId: string; userId: string; successUrl?: string; cancelUrl?: string }) =>
      request<{ sessionId: string; url: string }>('/api/stripe/checkout-session', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    createPortal: (payload: { userId: string; returnUrl?: string }) =>
      request<{ url: string }>('/api/stripe/portal-session', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  /* ─── Billing ─── */

  billing: {
    get: () =>
      request<{
        plan: string;
        subscriptionStatus: string;
        subscriptionId: string | null;
        currentPeriodEnd: string | null;
        cancelAtPeriodEnd: boolean;
        paymentMethod: { brand: string; last4: string; expMonth: number; expYear: number } | null;
        invoices: { date: string; description: string; amount: string; currency: string; status: string; pdfUrl: string | null }[];
        usage: { sites: number; experiments: number; visitors: number; limits: { sites: number; experiments: number; visitors: number } };
      }>('/api/billing', { method: 'GET' }),
  },

  /* ─── Evolution ─── */

  evolution: {
    status: () =>
      request<{
        status?: string;
        timestamp?: string;
        results?: { triggersFound: number; experimentsCreated: number; suggestionsAutoApproved: number; suggestionsPromoted: number; suggestionsRolledBack: number };
      }>('/api/evolve/status', { method: 'GET' }),

    triggers: () =>
      request<any[]>('/api/evolve/triggers', { method: 'GET' }),
  },

  /* ─── Trust ─── */

  trust: {
    get: () =>
      request<{
        profile?: { level: string; score: number; totalApproved: number; totalRejected: number; approvalRate: number; autoApproves: boolean };
      }>('/api/trust', { method: 'GET' }),
  },

  /* ─── Generic ─── */

  send: <T,>(endpoint: string, options?: RequestInit) => request<T>(endpoint, options),
};
